import React, { Component, ErrorInfo, ReactNode } from 'react'
import { motion } from 'framer-motion'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { useTranslation } from 'react-i18next'
import { useThemeStore } from '../../stores/theme'
import { logger } from '../../utils/logger'
import { analyticsService } from '../../services/analyticsService'
import { PRODUCT_EVENTS, errorOccurredParams } from '../../utils/productEvents'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  resetKeys?: Array<string | number>
  resetOnPropsChange?: boolean
  resetOnRouteChange?: boolean
  isolate?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  errorId: string
}

// A stale chunk (deployed after this tab loaded) fails to fetch under one of
// these messages, depending on browser and bundler. A reload picks up the new
// index.html and its current chunk hashes, which almost always fixes it.
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk .* failed/i

export function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false
  return error.name === 'ChunkLoadError' || CHUNK_LOAD_ERROR_PATTERN.test(error.message)
}

// Reload at most once per stale-chunk error so a persistent failure (offline,
// broken deploy) falls through to the normal error UI instead of loop-reloading.
const CHUNK_RELOAD_STORAGE_KEY = 'msb-chunk-error-reload-at'
const CHUNK_RELOAD_COOLDOWN_MS = 10_000

function shouldAttemptChunkReload(): boolean {
  try {
    const last = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)
    if (last && Date.now() - Number(last) < CHUNK_RELOAD_COOLDOWN_MS) {
      return false
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()))
    return true
  } catch {
    // sessionStorage unavailable (private mode, etc.) - allow one attempt.
    return true
  }
}

export class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    })

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.error('ErrorBoundary caught an error:', [error, errorInfo])
    }

    // Call custom error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    analyticsService.trackEvent({
      name: PRODUCT_EVENTS.error_occurred,
      parameters: {
        ...errorOccurredParams({
          surface: 'ErrorBoundary',
          error_code: isChunkLoadError(error) ? 'chunk_load' : (error.name || 'unhandled'),
        }),
        user_type: 'therapist',
      },
    }).catch(() => {})

    // Log error to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo)
    }

    if (isChunkLoadError(error) && shouldAttemptChunkReload()) {
      logger.warn('Stale chunk detected, reloading to fetch the current build', { error: error.message })
      window.location.reload()
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props
    const { hasError } = this.state

    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (resetOnPropsChange) {
        this.resetErrorBoundary()
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId)
    }
  }

  private logErrorToService = (error: Error, _errorInfo: ErrorInfo) => {
    // In a real application, you would send this to an error reporting service
    // like Sentry, LogRocket, or Bugsnag
    logger.error('ErrorBoundary production error', {
      errorId: this.state.errorId,
      error_code: error.name || 'unhandled',
      surface: 'ErrorBoundary',
    })
  }

  private resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId)
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    })
  }

  private handleRetry = () => {
    this.resetErrorBoundary()
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    const { hasError, error, errorInfo, errorId } = this.state
    const { children, fallback, isolate } = this.props

    if (hasError) {
      if (fallback) {
        return fallback
      }

      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          errorId={errorId}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
          isolate={isolate}
        />
      )
    }

    return children
  }
}

interface ErrorFallbackProps {
  error: Error | null
  errorInfo: ErrorInfo | null
  errorId: string
  onRetry: () => void
  onReload: () => void
  onGoHome: () => void
  isolate?: boolean
}

function ErrorFallback({
  error,
  errorInfo,
  errorId,
  onRetry,
  onReload,
  onGoHome,
  isolate = false
}: ErrorFallbackProps) {
  const { t } = useTranslation(['common', 'errors'])
  const themeType = useThemeStore((s) => s.themeType)
  const isChildTheme = themeType === 'child'

  const containerClasses = isolate
    ? "min-h-screen flex items-center justify-center p-4"
    : "p-6"

  return (
    <div className={containerClasses}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-2xl mx-auto"
      >
        <Card className="p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mb-6"
          >
            <div className={`
              w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4
              ${isChildTheme 
                ? 'bg-red-100 text-red-600' 
                : 'bg-red-50 text-red-500'
              }
            `}>
              <AlertTriangle className="h-8 w-8" />
            </div>
          </motion.div>

          <h1 className={`
            text-2xl font-bold mb-2
            ${isChildTheme ? 'text-child-primary font-child' : 'text-therapist-primary font-therapist'}
          `}>
            {t('errors:boundary.title')}
          </h1>

          <p className={`
            text-lg mb-6
            ${isChildTheme ? 'text-child-textMuted' : 'text-therapist-textMuted'}
          `}>
            {t('errors:boundary.description')}
          </p>

          {process.env.NODE_ENV === 'development' && error && (
            <details className="mb-6 text-start">
              <summary className="cursor-pointer font-medium mb-2">
                {t('errors:boundary.details')}
              </summary>
              <div className="bg-gray-100 p-4 rounded-md text-sm font-mono overflow-auto">
                <div className="mb-2">
                  <strong>Error:</strong> {error.message}
                </div>
                <div className="mb-2">
                  <strong>Stack:</strong>
                  <pre className="whitespace-pre-wrap">{error.stack}</pre>
                </div>
                {errorInfo && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="whitespace-pre-wrap">{errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={onRetry}
              className="flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>{t('errors:boundary.retry')}</span>
            </Button>

            <Button
              onClick={onGoHome}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <Home className="h-4 w-4" />
              <span>{t('errors:boundary.goHome')}</span>
            </Button>

            <Button
              onClick={onReload}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>{t('errors:boundary.reload')}</span>
            </Button>
          </div>

          {process.env.NODE_ENV === 'production' && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-600 mb-2">
                {t('errors:boundary.reportId')}
              </p>
              <code className="text-xs font-mono bg-white px-2 py-1 rounded border">
                {errorId}
              </code>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  )
}

// Higher-order component for easier usage
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  )

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`
  return WrappedComponent
}

// Hook for error boundary context
export function useErrorHandler() {
  return (error: Error, errorInfo?: ErrorInfo) => {
    // This can be used to manually trigger error boundary
    logger.error('Manual error trigger:', [error, errorInfo])
  }
}

// Route-level error boundary
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      resetOnRouteChange
      onError={(error, errorInfo) => {
        // Log route-level errors
        logger.error('Route error:', [error, errorInfo])
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

// Feature-level error boundary
export function FeatureErrorBoundary({ 
  children, 
  feature 
}: { 
  children: ReactNode
  feature: string 
}) {
  return (
    <ErrorBoundary
      isolate
      onError={(error, errorInfo) => {
        logger.error(`Feature error in ${feature}:`, [error, errorInfo])
      }}
    >
      {children}
    </ErrorBoundary>
  )
}
